import { z } from "zod";
import {
  nullableKcFromForm,
  nullableUuid,
  optionalKcFromForm,
  optionalUuid,
} from "../lib/zod-helpers";
import { paginationSchema, sortSchema } from "./pagination";

const uuidMsg = "Neplatné UUID";

/**
 * HCA Moravia — validační schémata pro `hca.products` tRPC router.
 * Stringové fieldy mají `.max()` per repo konvenci (scripts/check-schema-max.ts).
 */

/** Povolené kategorie produktů. Drizzle drží `kategorie` jako text; enum
 *  enforce-uje Zod na vstupu. */
export const productKategorieEnum = z.enum([
  "komunikace",
  "obchod",
  "leadership",
  "management",
  "ostatni",
]);
export type ProductKategorie = z.infer<typeof productKategorieEnum>;

/** Lowercase ASCII slug pro veřejné landing pages. */
export const productSlugSchema = z
  .string()
  .min(2, "Slug musí mít alespoň 2 znaky")
  .max(100, "Slug musí být 100 znaků nebo méně")
  .regex(/^[a-z0-9-]+$/, "Slug může obsahovat pouze malá písmena, čísla a pomlčky");

/** Schema pro vytvoření produktu. `slug` generuje server z `nazev` při create. */
export const createProductSchema = z.object({
  nazev: z.string().min(1, "Název je povinný").max(255, "Název musí být 255 znaků nebo méně"),
  kategorie: productKategorieEnum,
  cenaKatalog: z.coerce
    .number()
    .nonnegative("Cena nesmí být záporná")
    .max(10_000_000, "Cena je mimo povolený rozsah"),
  cenaPerUcastnik: z.coerce
    .number()
    .nonnegative("Cena nesmí být záporná")
    .max(10_000_000, "Cena je mimo povolený rozsah")
    .optional(),
  delkaDnu: z.coerce
    .number()
    .multipleOf(0.5, "Délka musí být v půldenních krocích (0,5)")
    .min(0.5, "Délka musí být alespoň půlden (0,5)")
    .max(365, "Délka musí být 365 dnů nebo méně"),
  popis: z.string().max(10000, "Popis musí být 10000 znaků nebo méně").optional(),
  defaultMisto: z.string().max(500, "Místo musí být 500 znaků nebo méně").optional(),
  licencePct: z.coerce
    .number()
    .min(0, "Licence nesmí být záporná")
    .max(100, "Licence nesmí přesáhnout 100 %")
    .optional(),
  /**
   * Licence — odpočet Kč na osobu a den (model majitele, 2026-08). Licenční
   * poplatek se počítá z hodnoty semináře po odečtení této částky za každého
   * účastníka a den. Prázdný input ("") = nevyplněno (server doplní default
   * 1000), NIKDY se nesmí tiše koercovat na 0 — money-path.
   */
  licenceOdpocetKcOsDen: optionalKcFromForm(1_000_000, "Odpočet musí být 0 až 1 000 000 Kč"),
  /**
   * Jeden souhrnný fixní náklad na účastníka (Kč). Prázdný input ("") / null
   * = nenastaveno (NULL) — v ekonomice termínu pak platí per-termín hodnota / 0.
   * V update kontextu vynechání pole = beze změny.
   */
  fixniNakladNaOsobuKc: nullableKcFromForm(10_000_000, "Náklad musí být 0 až 10 000 000 Kč"),
  /** Štítek, který se absolventovi přiřadí automaticky po zapsání docházky. */
  defaultAbsolventTagId: optionalUuid(uuidMsg),
  /** E-mailová série spouštěná automaticky po zapsání docházky (trigger PO_DOCHAZCE). */
  defaultAfterAttendSerialId: optionalUuid(uuidMsg),
});

/**
 * Partial update + required id. U auto-akcí rozlišujeme `undefined`
 * (beze změny) od `null` / prázdné volby v selectu (odpojit štítek či sérii).
 */
export const updateProductSchema = createProductSchema.partial().extend({
  id: z.string().uuid("Neplatné ID produktu"),
  defaultAbsolventTagId: nullableUuid(uuidMsg),
  defaultAfterAttendSerialId: nullableUuid(uuidMsg),
});

/** Whitelist řazení produktů — router mapuje klíče na sloupce. */
export const PRODUCTS_SORT_KEYS = ["nazev", "cena", "delka", "aktivni", "vytvoreno"] as const;
export type ProductsSortKey = (typeof PRODUCTS_SORT_KEYS)[number];

/** List s filtrem a paginací. */
export const listProductsSchema = paginationSchema.merge(sortSchema(PRODUCTS_SORT_KEYS)).extend({
  kategorie: productKategorieEnum.optional(),
  aktivni: z.coerce.boolean().optional(),
  search: z.string().max(255, "Hledaný text musí být 255 znaků nebo méně").optional(),
});

/** ID-only schema pro getById / archive / restore. */
export const productIdSchema = z.object({
  id: z.string().uuid("Neplatné ID produktu"),
});

/** Veřejný list filter (publicProcedure na landing pages). */
/** Input pro veřejné `getBySlug`. */
export const publicProductBySlugSchema = z.object({
  slug: productSlugSchema,
});
export type PublicProductBySlugInput = z.infer<typeof publicProductBySlugSchema>;

export const listPublicProductsSchema = z.object({
  kategorie: productKategorieEnum.optional(),
});

export type CreateProductInput = z.infer<typeof createProductSchema>;
export type UpdateProductInput = z.infer<typeof updateProductSchema>;
/**
 * Form-side (nezparsovaný) tvar pro react-hook-form: Kč pole z inputů můžou
 * být string ("" = nevyplněno) — parsování řeší zodResolver. Použít jako
 * `useForm<CreateProductFormInput, unknown, CreateProductInput>`.
 */
export type CreateProductFormInput = z.input<typeof createProductSchema>;
export type UpdateProductFormInput = z.input<typeof updateProductSchema>;
export type ListProductsInput = z.infer<typeof listProductsSchema>;
export type ProductIdInput = z.infer<typeof productIdSchema>;
export type ListPublicProductsInput = z.infer<typeof listPublicProductsSchema>;
